/**
 * Brand & category performance - /sales/brands (SalesHubPage renders only the tabs above it).
 *
 *   <SalesBrandsPage />                      owns its scope: renders the h1 and SalesControlBar itself
 *   <SalesBrandsPage scope={scope} setScope={setScope} />   parent owns the header and control bar
 *
 * Loads GET /api/sales-hub/leaderboard for the page scope. KPI row for the scope (revenue, units, ASP, return
 * rate with the change), then a sortable table per brand or category: revenue + share, change in ₹ and %,
 * units, average selling price, return rate, SKUs sold, and the comparison-period revenue. Each row expands
 * to its categories (or brands) with share and change. Clicking a name (or a bar in the top-8 chart) applies
 * it as a page filter via setScope; the page then offers "See products" and "Open in Business Review".
 * The leaderboard is fetched without its own grouping filter (brand in "By brand", category in "By category"),
 * so applying a brand keeps the full ranking and just highlights that row. Names are the catalogue's canonical
 * spelling (displayName), so "baby gear" and "Baby Gear" are one row. CSV export of the table.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowRight, ChevronDown, ChevronRight, Download, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { LoadError } from '../components/LoadError';
import { SortHeader, useTableControls } from '../components/TableControls';
import { useSalesData } from '../components/sales/salesApi';
import { DeltaBadge } from '../components/sales/DeltaBadge';
import { KpiCard } from '../components/sales/KpiCard';
import { ShareBar } from '../components/sales/ShareBar';
import { compareWords, dayCount, displayName, downloadCsv, number, shortMoney } from '../components/sales/format';
import {
  CLEAR_FILTERS, EmptyState, MetricToggle, TableSkeleton, ViewHeader, hasFilters, pctText, previousFromChange, usePageScope, useScopeLink,
  useSalesOptions,
} from '../components/sales/views/common';

const MODES = [['brand', 'By brand'], ['category', 'By category']];
const CHILD = { brand: 'category', category: 'brand' };
const CHART_TOP = 8;
const truncate = (text, size = 16) => (text.length > size ? `${text.slice(0, size - 1)}…` : text);

const hasMoney = item => Boolean(item.revenue || item.previous_revenue);

const childRow = (child, mode) => {
  const revenue = child.revenue || 0;
  const previous = child.previous_revenue ?? previousFromChange(revenue, child.revenue_change);
  return {
    name: displayName(child[CHILD[mode]]),
    revenue,
    previous_revenue: previous,
    // Sold last period, nothing now: brands inside a category come with -100% but no previous value.
    lost: previous == null && !revenue && Number(child.revenue_change) === -100,
    share: mode === 'brand' ? child.share_of_brand : child.share_of_category,
    units: child.units,
  };
};

const normalise = (board, mode) => {
  const list = mode === 'brand' ? board?.brands : board?.categories;
  // Hide groups with no money in either period; a returns-only top-level group stays visible.
  return (list || []).filter(item => hasMoney(item) || item.returned_units).map(item => {
    const revenue = item.revenue || 0;
    const previous = item.previous_revenue || 0;
    return {
      name: displayName(item[mode]),
      revenue,
      previous_revenue: previous,
      delta: revenue - previous,
      change_pct: previous ? ((revenue - previous) / previous) * 100 : (revenue ? null : 0),
      share: item.share || 0,
      units: item.units || 0,
      previous_units: item.previous_units || 0,
      asp: item.avg_selling_price ?? (item.units ? revenue / item.units : null),
      returned_units: item.returned_units || 0,
      return_rate: item.return_rate,
      sku_count: item.sku_count,
      children: ((mode === 'brand' ? item.categories : item.brands) || [])
        .filter(child => hasMoney(child) || Number(child.revenue_change) === -100)
        .map(child => childRow(child, mode)),
    };
  });
};

const SORT_VALUE = { name: row => row.name.toLowerCase(), change_pct: row => (row.change_pct == null ? -Infinity : row.change_pct) };
const SEARCH_KEYS = ['name'];

export default function SalesBrandsPage(props) {
  const { scope, setScope, salesScope } = usePageScope(props);
  const scopeLink = useScopeLink();
  const [mode, setMode] = useState(scope.category !== 'ALL' && scope.brand === 'ALL' ? 'category' : 'brand');
  // Leave out the grouping dimension's own filter so the ranking stays a ranking after a click.
  const { data, loading, error, refresh } = useSalesData('leaderboard', scope, { [mode]: null });
  const options = useSalesOptions(scope.country, Boolean(salesScope));
  const [expanded, setExpanded] = useState(() => new Set());
  const days = dayCount(scope.date_from, scope.date_to);
  const vs = compareWords(scope.compare, days);

  const rows = useMemo(() => normalise(data, mode), [data, mode]);
  const table = useTableControls(rows, { searchKeys: SEARCH_KEYS, initialSort: { key: 'revenue', dir: 'desc' }, pageSize: 500, sortValue: SORT_VALUE });
  const rank = useMemo(() => new Map(rows.map((row, index) => [row.name, index + 1])), [rows]);
  const childMode = CHILD[mode];
  const filtered = scope[mode] && scope[mode] !== 'ALL' ? scope[mode] : null;
  const chartData = useMemo(() => rows.filter(row => row.revenue > 0 || row.previous_revenue > 0).slice(0, CHART_TOP)
    .map(row => ({ name: row.name, revenue: row.revenue, previous_revenue: row.previous_revenue })), [rows]);

  const apply = (name, patch = {}) => setScope(scope[mode] === name && !Object.keys(patch).length ? { [mode]: '' } : { [mode]: name, ...patch });
  const toggle = name => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });
  // Open the filtered row once when the filter changes; its chevron can still close it.
  useEffect(() => {
    if (filtered) setExpanded(prev => (prev.has(filtered) ? prev : new Set(prev).add(filtered)));
  }, [filtered]);
  const isOpen = name => expanded.has(name);

  const totals = useMemo(() => data?.totals || {}, [data]);
  const summary = useMemo(() => {
    const units = rows.reduce((sum, row) => sum + row.units, 0);
    const previousUnits = rows.reduce((sum, row) => sum + row.previous_units, 0);
    const returned = rows.reduce((sum, row) => sum + row.returned_units, 0);
    return {
      units, previousUnits, returned,
      asp: units ? (totals.revenue || 0) / units : null,
      previousAsp: previousUnits ? (totals.previous_revenue || 0) / previousUnits : null,
      returnRate: units ? (returned / units) * 100 : null,
    };
  }, [rows, totals]);

  const exportCsv = () => {
    const label = mode === 'brand' ? 'Brand' : 'Category';
    const head = [label, 'Revenue', 'Comparison revenue', 'Change', 'Change %', 'Share %', 'Units', 'Average selling price', 'Returned units', 'Return rate %', 'SKUs sold'];
    const body = table.all.map(row => [row.name, row.revenue, row.previous_revenue, row.delta, row.change_pct == null ? '' : row.change_pct.toFixed(1),
      row.share, row.units, row.asp == null ? '' : Math.round(row.asp), row.returned_units, row.return_rate ?? '', row.sku_count ?? '']);
    downloadCsv([head, ...body], `${mode}-performance-${scope.date_from}-to-${scope.date_to}.csv`);
  };

  const header = (
    <ViewHeader salesScope={salesScope} title="Brands & categories" testId="sales-brands-heading"
      brands={options.brands} categories={options.categories} onRefresh={refresh} loading={loading} />
  );

  if (error && !data) {
    return (
      <div className="space-y-4" data-testid="sales-brands-page">
        {header}
        <LoadError what="the brand leaderboard" error={error} onRetry={refresh} retrying={loading} testId="sales-brands-error" />
      </div>
    );
  }

  const label = mode === 'brand' ? 'brand' : 'category';
  const reviewLink = `/review?${new URLSearchParams(Object.fromEntries(Object.entries({ platform: scope.platform, brand: scope.brand, category: scope.category })
    .filter(([, value]) => value && value !== 'ALL'))).toString()}`;

  return (
    <div className="space-y-4" data-testid="sales-brands-page">
      {header}
      {error && <LoadError what="the latest leaderboard" error={error} onRetry={refresh} retrying={loading} />}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="leaderboard-kpis">
        <KpiCard label="Revenue" value={data ? shortMoney(totals.revenue) : null} current={totals.revenue} previous={totals.previous_revenue} compareLabel={vs} loading={loading} testId="leaderboard-kpi-revenue" />
        <KpiCard label="Units" value={data ? number(summary.units) : null} current={summary.units} previous={summary.previousUnits} kind="number" compareLabel={vs} loading={loading} testId="leaderboard-kpi-units" />
        <KpiCard label="Average selling price" value={data ? (summary.asp == null ? '—' : shortMoney(summary.asp)) : null} current={summary.asp} previous={summary.previousAsp} compareLabel={vs} loading={loading} testId="leaderboard-kpi-asp" />
        <KpiCard label="Return rate" value={data ? pctText(summary.returnRate) : null} current={summary.returnRate} previous={undefined} loading={loading}
          footnote={summary.returned ? `${number(summary.returned)} units returned in the period` : 'No returns recorded in the period'} testId="leaderboard-kpi-returns" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <MetricToggle value={mode} onChange={setMode} options={MODES} label="Group leaderboard" />
          <input type="search" value={table.query} onChange={e => table.setQuery(e.target.value)} placeholder={`Find a ${label}`} aria-label={`Find a ${label}`}
            className="h-7 w-40 rounded-md border border-gray-300 px-2 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-testid="leaderboard-search" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to={reviewLink} className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-testid="leaderboard-open-review">
            Open in Business Review <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={exportCsv} disabled={!rows.length} data-testid="leaderboard-export">
            <Download className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> CSV
          </Button>
        </div>
      </div>

      {(scope.brand !== 'ALL' || scope.category !== 'ALL') && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900" role="status" data-testid="leaderboard-filter-banner">
          <span>Filtered to <strong>{[scope.brand, scope.category].filter(value => value && value !== 'ALL').join(' · ')}</strong></span>
          <Link to={scopeLink('/sales/skus', {})} className="inline-flex items-center gap-1 font-medium text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-testid="leaderboard-see-products">
            See products <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
          <button type="button" onClick={() => setScope({ brand: '', category: '' })} aria-label="Clear brand and category filter"
            className="ml-auto inline-flex items-center gap-1 rounded px-1.5 text-xs font-medium text-blue-800 hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
            <X className="h-3.5 w-3.5" aria-hidden="true" />Clear
          </button>
        </div>
      )}

      {!data ? (
        <div className="grid gap-4 lg:grid-cols-5"><div className="lg:col-span-3"><TableSkeleton rows={6} label="Loading leaderboard" /></div><Skeleton className="h-72 lg:col-span-2" /></div>
      ) : !rows.length ? (
        <EmptyState title="No sales in this period" testId="sales-brands-empty"
          action={hasFilters(scope) && <Button size="sm" variant="outline" onClick={() => setScope(CLEAR_FILTERS)}>Clear filters</Button>}>
          {hasFilters(scope) ? 'Nothing matches these filters. Clear them or pick a longer period.' : 'Pick a longer period, or upload sales from the overview tab.'}
        </EmptyState>
      ) : (
        <div className={`grid gap-4 transition-opacity lg:grid-cols-5 ${loading ? 'opacity-60' : ''}`} aria-busy={loading}>
          <section className="min-w-0 rounded-xl border border-gray-200 bg-white lg:col-span-3" aria-labelledby="leaderboard-title">
            <h2 id="leaderboard-title" className="border-b border-gray-100 px-4 py-3 text-sm font-semibold text-gray-900">
              {mode === 'brand' ? 'Brands' : 'Categories'} · {table.total} of {rows.length}
              <span className="ml-2 text-xs font-normal text-gray-500">Select one to filter the page · sort by any column</span>
            </h2>
            <div className="relative overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm" data-testid="leaderboard-table">
                <caption className="sr-only">{mode === 'brand' ? 'Brands' : 'Categories'} by revenue with share, change {vs}, units, average selling price, return rate and SKUs sold.</caption>
                <thead className="bg-gray-50 text-xs text-gray-600">
                  <tr>
                    <th scope="col" className="w-10 px-3 py-2 text-left font-medium">#</th>
                    <SortHeader label={`${mode === 'brand' ? 'Brand' : 'Category'} · revenue · share`} sortKey="name" table={table} className="px-1" />
                    <SortHeader label="Change" sortKey="delta" table={table} align="right" />
                    <SortHeader label="%" sortKey="change_pct" table={table} align="right" />
                    <SortHeader label="Units" sortKey="units" table={table} align="right" />
                    <SortHeader label="ASP" sortKey="asp" table={table} align="right" />
                    <SortHeader label="Returns" sortKey="return_rate" table={table} align="right" />
                    <SortHeader label="SKUs" sortKey="sku_count" table={table} align="right" />
                    <th scope="col" className="w-10 px-2 py-2"><span className="sr-only">Expand</span></th>
                  </tr>
                </thead>
                <tbody>
                  {table.pageRows.map(row => {
                    const open = isOpen(row.name);
                    const childrenId = `lb-children-${rank.get(row.name)}`;
                    return (
                      <React.Fragment key={row.name}>
                        <tr className={`border-t border-gray-100 ${filtered === row.name ? 'bg-blue-50/40' : ''}`} data-testid={`leaderboard-row-${row.name}`}>
                          <td className="px-3 py-2 text-xs tabular-nums text-gray-500">{rank.get(row.name)}</td>
                          <td className="min-w-[220px] px-1 py-1">
                            <ShareBar label={row.name} value={shortMoney(row.revenue)} share={row.share} active={filtered === row.name}
                              sublabel={row.previous_revenue ? `${shortMoney(row.previous_revenue)} ${vs.replace(/^vs /, 'in the ')}` : (row.revenue ? null : 'No sales this period')}
                              onClick={() => apply(row.name)}
                              ariaLabel={filtered === row.name ? `Remove ${label} filter ${row.name}` : `Filter the page to ${label} ${row.name}`} />
                          </td>
                          <td className="px-3 py-2 text-right"><DeltaBadge current={row.revenue} previous={row.previous_revenue} showPct={false} size="xs" /></td>
                          <td className={`px-3 py-2 text-right text-xs tabular-nums ${row.change_pct == null ? 'text-sky-700' : row.change_pct > 0 ? 'text-green-700' : row.change_pct < 0 ? 'text-red-700' : 'text-gray-500'}`}>
                            {row.change_pct == null ? 'New' : `${row.change_pct > 0 ? '+' : ''}${row.change_pct.toFixed(1)}%`}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{number(row.units)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{row.asp == null ? '—' : shortMoney(row.asp)}</td>
                          <td className={`px-3 py-2 text-right tabular-nums ${row.return_rate >= 10 ? 'text-red-700' : 'text-gray-700'}`}
                            title={row.returned_units ? `${number(row.returned_units)} units returned` : undefined}>
                            {row.returned_units ? pctText(row.return_rate) : '—'}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{row.sku_count ? number(row.sku_count) : '—'}</td>
                          <td className="px-2 py-2 text-right">
                            {row.children.length > 0 && (
                              <button type="button" onClick={() => toggle(row.name)} aria-expanded={open} aria-controls={childrenId}
                                aria-label={`${open ? 'Hide' : 'Show'} ${childMode === 'brand' ? 'brands' : 'categories'} in ${row.name}`}
                                className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                                {open ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
                              </button>
                            )}
                          </td>
                        </tr>
                        {open && row.children.length > 0 && (
                          <tr id={childrenId} className="bg-gray-50/60">
                            <td aria-hidden="true" />
                            <td colSpan={8} className="px-1 pb-3 pt-1">
                              <p className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                                {childMode === 'brand' ? 'Brands' : 'Categories'} in {row.name} · share of {row.name}
                              </p>
                              <ul className="space-y-0.5">
                                {row.children.map(child => (
                                  <li key={child.name}>
                                    <ShareBar label={child.name} value={shortMoney(child.revenue)} share={child.share} color="#64748B"
                                      sublabel={child.units ? `${number(child.units)} units` : null}
                                      extra={child.lost
                                        ? <span className="inline-flex whitespace-nowrap rounded-full bg-red-50 px-1.5 text-[11px] font-medium text-red-800 ring-1 ring-inset ring-red-200" title="Sold in the comparison period, nothing this period">Lost</span>
                                        : <DeltaBadge current={child.revenue} previous={child.previous_revenue} size="xs" />}
                                      onClick={() => apply(row.name, { [childMode]: child.name })}
                                      ariaLabel={`Filter the page to ${row.name} and ${child.name}`} />
                                  </li>
                                ))}
                              </ul>
                              <Link to={scopeLink('/sales/skus', { [mode]: row.name })}
                                className="ml-2 mt-1 inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                                See {row.name} products <ArrowRight className="h-3 w-3" aria-hidden="true" />
                              </Link>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section className="min-w-0 rounded-xl border border-gray-200 bg-white p-4 lg:col-span-2" aria-labelledby="leaderboard-chart-title">
            <h2 id="leaderboard-chart-title" className="text-sm font-semibold text-gray-900">Top {Math.min(CHART_TOP, chartData.length)} {mode === 'brand' ? 'brands' : 'categories'}: this period vs comparison</h2>
            <p className="mb-2 text-xs text-gray-500">Comparison is {vs.replace(/^vs /, 'the ')}. Click a bar to filter.</p>
            <div role="img" aria-label={`Revenue this period and in the comparison period for the top ${mode === 'brand' ? 'brands' : 'categories'}: ${chartData.map(row => `${row.name} ${shortMoney(row.revenue)} vs ${shortMoney(row.previous_revenue)}`).join('; ')}`}
              style={{ height: Math.max(160, chartData.length * 44 + 48) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }} barGap={2}>
                  <CartesianGrid strokeDasharray="3 6" horizontal={false} stroke="#e5e7eb" />
                  <XAxis type="number" tickFormatter={shortMoney} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={104} tickFormatter={name => truncate(String(name))} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(value, name) => [shortMoney(value), name]} cursor={{ fill: '#f1f5f9' }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="revenue" name="This period" fill="#2563EB" radius={[0, 3, 3, 0]} maxBarSize={14} isAnimationActive={false}
                    cursor="pointer" onClick={entry => { const name = entry?.payload?.name ?? entry?.name; if (name) apply(name); }} />
                  <Bar dataKey="previous_revenue" name="Comparison" fill="#CBD5E1" radius={[0, 3, 3, 0]} maxBarSize={14} isAnimationActive={false}
                    cursor="pointer" onClick={entry => { const name = entry?.payload?.name ?? entry?.name; if (name) apply(name); }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-3 text-xs text-gray-500">For week-on-week, year-on-year and the reasons behind each move (price, stock, ads, returns), open the {label} in the Business Review.</p>
          </section>
        </div>
      )}
    </div>
  );
}
